import { Button, Col, Form, Input, Row } from "antd";
import { useStoreFormStructureMutation } from "api/TenantSlug/FormStructure/hooks";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { CategorySelectSearchable } from "components/UI/Selects/CategorySelectSearchable/CategorySelectSearchable.tsx";
import { CountrySelectSearchable } from "components/UI/Selects/CountrySelectSearchable/CountrySelectSearchable.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useUser } from "hooks/useUser";
import { TOAST_TYPES } from "localConstants";
import { FC } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { FormStructureType } from "types/enums.ts";
import { showCustomToast } from "utils/toast";

import { FormStructureData, schema } from "./formSchema";

import StoreFormStructureRequest = App.Http.Requests.FormStructure.StoreFormStructureRequest;

interface FormStructureAddFormProps {
    type: FormStructureType;
    onSubmit?: () => void;
    formId?: string;
}

const FormStructureContent: FC<FormStructureAddFormProps> = () => {
    const { t: tC } = usePaldockTranslation("common");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const generalFormUtils = useGeneralForm<FormStructureData>();
    const { control } = generalFormUtils;

    return (
        <>
            <Row gutter={16}>
                <Col span={24}>
                    <FormItem control={control} name="name" label={tFields("name.label")} required>
                        <Input placeholder={tFields("name.placeholder")} />
                    </FormItem>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={24}>
                    <FormItem control={control} name="system_name" label={tFields("system_name.label")} required>
                        <Input placeholder={tFields("system_name.placeholder")} />
                    </FormItem>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={24}>
                    <FormItem control={control} name="country_id" label={tFields("country.label")}>
                        <CountrySelectSearchable allowClear />
                    </FormItem>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={24}>
                    <FormItem control={control} name="category" label={tFields("category.label")}>
                        <CategorySelectSearchable allowClear />
                    </FormItem>
                </Col>
            </Row>
            <Form.Item>
                <Button type="primary" htmlType="submit">
                    {tC("add")}
                </Button>
            </Form.Item>
        </>
    );
};

export const FormStructureForm: FC<FormStructureAddFormProps> = ({ type, onSubmit }) => {
    const { t } = usePaldockTranslation("forms.form-structure");
    const { t: tC } = usePaldockTranslation("common");
    const defaultValues: FormStructureData = { name: "", system_name: "", country_id: null, category: null };
    const storeFormStructureMutation = useStoreFormStructureMutation();

    const userUtils = useUser();

    const onSubmitHandler = (values: FormStructureData) => {
        const storeFormStructureRequest = {
            id: null,
            system_name: values.system_name,
            name: values.name,
            formFields: [],
            type,
            country_id: values.country_id ?? null,
            category_id: values.category ?? null,
        } as StoreFormStructureRequest;

        storeFormStructureMutation.mutate(storeFormStructureRequest, {
            onSuccess: (response) => {
                if (response && response.formStructure) {
                    showCustomToast(
                        {
                            title: t("messages.create_success"),
                            buttons: [
                                {
                                    label: tC("open"),
                                    href: response?.formStructure?.id
                                        ? `/${userUtils.getActiveTenant()?.slug_name}/tool/structure/${type}/${response.formStructure.id}`
                                        : undefined,
                                },
                            ],
                        },
                        TOAST_TYPES.SUCCESS
                    );
                    onSubmit?.();
                }
            },
            onError: (error) => {
                toast.error(`${t("messages.create_error")}: ${error.message}`);
            },
        });
    };

    return (
        <div className="max-w-xl">
            <GeneralFormProvider<FormStructureData>
                schema={schema}
                defaultValues={defaultValues}
                onSubmit={onSubmitHandler}
            >
                <FormStructureContent type={type} onSubmit={onSubmit} />
            </GeneralFormProvider>
        </div>
    );
};
